import type { DataStore } from "../../Data/Store"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import type { IShipUser, IShipUserResponse } from "../../Types/Generated/LogbookModel/ShipUser"
import type { IShipUserRequest } from "../../Types/Generated/LogbookApi/UserApi"
import type { ISuccessResponse } from "../../Types/Generated/LogbookServer/SuccessResponse"
import { UserApi } from "../../Types/Generated/LogbookApi/UserApi"

// Identifiers a crew-writer bundle is scoped to: the one ship whose ShipUser roster it
// edits. The ShipUser row id is a per-call argument (a ship holds many crew members,
// each edited/removed independently).
export interface ShipUserWriterIds {
  shipId: number;
}

// Values for a crew update: the generated `IShipUserRequest` plus the optional `userId`
// re-invite field the update route accepts (setting it to `null` re-issues an
// invitation). Mirrors the legacy `ShipUserViewData.updateShipUser` value type.
export type ShipUserUpdateValues = IShipUserRequest & { userId?: number | null };

// The write surface a UsersView acquisition variant provides to its crew edit sub-views.
// Both variants expose the same shape: the New (flag-on) variant backs these with
// optimistic `DataStore.mutate` writes reconciled by the server response/broadcast; the
// Legacy (flag-off) variant backs them with plain typed-REST calls. Edit views call
// `useShipUserWriters().removeShipUser(...)` rather than the typed client directly, so
// the data-acquisition layer owns the write path (mirrors Day's `DayWriters`, #1165).
export interface ShipUserWriters {
  createShipUser: (values: IShipUserRequest) => Promise<IShipUserResponse>;
  updateShipUser: (userId: number, values: ShipUserUpdateValues) => Promise<IShipUserResponse>;
  removeShipUser: (userId: number) => Promise<ISuccessResponse>;
}

// Project a crew update onto the stored row. `userId: null` is a wire sentinel meaning
// "re-issue the invitation" (the route detaches the account), which the stored `IShipUser`
// spells as an absent `userId` — so the optimistic row must carry `undefined`, not the
// `null` that only the request body understands.
function optimisticShipUser(current: IShipUser, values: ShipUserUpdateValues): IShipUser {
  const { userId, ...rest } = values;
  return { ...current, ...rest, userId: userId === undefined ? current.userId : userId ?? undefined };
}
// optimistic row (there is nothing to show until the server assigns the id) and reconciles
// the returned crew member; `update` lands the field edit optimistically and reconciles the
// authoritative row; `remove` — this slice's DELETE exemplar — optimistically drops the row,
// carries no reconcile (the delete response holds no crew member), and rolls the removal back
// (guarded against a racing broadcast) if the request rejects. The typed endpoints are the
// single source of the wire contract, shared with the legacy variant below.
export function createStoreShipUserWriters(store: DataStore, ids: ShipUserWriterIds): ShipUserWriters {
  const { shipId } = ids;
  return {
    createShipUser(values) {
      return store.mutate<IShipUserResponse>({
        request: (client) => new UserApi(client).shipUsers.create({ params: { shipId }, body: values }).call(),
        reconcile: (r) => ({ entities: { ShipUser: [r.user] } }),
      });
    },
    updateShipUser(userId, values) {
      const current = store.getEntity(SchemaEntities.ShipUser, userId);
      const optimistic = current ? { upsert: { ShipUser: [optimisticShipUser(current, values)] } } : undefined;
      return store.mutate<IShipUserResponse>({
        optimistic,
        request: (client) => new UserApi(client).shipUsers.update({ params: { shipId, userId }, body: values }).call(),
        reconcile: (r) => ({ entities: { ShipUser: [r.user] } }),
      });
    },
    removeShipUser(userId) {
      return store.mutate<ISuccessResponse>({
        optimistic: { remove: { ShipUser: [userId] } },
        request: (client) => new UserApi(client).shipUsers.delete({ params: { shipId, userId } }).call(),
      });
    },
  };
}

// Inert crew writers for harnesses/tests that render a crew subtree but never exercise a
// write (the real variants provide working writers). Each rejects loudly if unexpectedly
// called, so an accidental save/delete in a read-only fixture surfaces rather than silently
// no-opping.
const rejectNoop = (name: string) => (): Promise<never> =>
  Promise.reject(new Error(`noopShipUserWriters.${name}: no writer provided (read-only crew context)`));

export const noopShipUserWriters: ShipUserWriters = {
  createShipUser: rejectNoop("createShipUser"),
  updateShipUser: rejectNoop("updateShipUser"),
  removeShipUser: rejectNoop("removeShipUser"),
};
